import { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncIcon from '@mui/icons-material/Sync';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useApi } from '../../hooks/useApi';
import { StoredTeam, StoredMember } from '../../types/teams';

interface TeamMemberManagementProps {
  agentId: string | number;
  isConnected: boolean;
}

export const TeamMemberManagement = ({ agentId, isConnected }: TeamMemberManagementProps) => {
  const { apiFetch } = useApi();
  
  const [teams, setTeams] = useState<StoredTeam[]>([]);
  const [membersByTeam, setMembersByTeam] = useState<Record<number, StoredMember[]>>({});
  const [expandedTeams, setExpandedTeams] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [syncingTeams, setSyncingTeams] = useState(false);
  const [syncingMembers, setSyncingMembers] = useState<number | null>(null);
  const [togglingTeam, setTogglingTeam] = useState<number | null>(null);
  const [togglingMember, setTogglingMember] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch stored teams
  const fetchTeams = useCallback(async () => {
    if (!isConnected) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await apiFetch(`agents/${agentId}/teams/stored`);
      if (res.ok) {
        const data = await res.json();
        setTeams(data);
      } else {
        const errData = await res.json();
        setError(errData.error || 'Failed to fetch teams');
      }
    } catch (err) {
      setError('Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  }, [agentId, isConnected, apiFetch]);

  // Fetch members for a specific team
  const fetchMembers = async (teamId: number) => {
    try {
      const res = await apiFetch(`agents/${agentId}/teams/stored/${teamId}/members`);
      if (res.ok) {
        const data = await res.json();
        setMembersByTeam(prev => ({ ...prev, [teamId]: data }));
      }
    } catch (err) {
      console.error('Failed to fetch members:', err);
    }
  };

  // Sync teams from Microsoft
  const handleSyncTeams = async () => {
    setSyncingTeams(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const res = await apiFetch(`agents/${agentId}/teams/sync`, { method: 'POST' });
      const data = await res.json();
      
      if (data.success) {
        setSuccessMessage(data.message);
        await fetchTeams();
      } else {
        setError(data.error || 'Failed to sync teams');
      }
    } catch (err) {
      setError('Failed to sync teams from Microsoft');
    } finally {
      setSyncingTeams(false);
    }
  };

  // Sync members for a team
  const handleSyncMembers = async (teamId: number) => {
    setSyncingMembers(teamId);
    setError(null);
    
    try {
      const res = await apiFetch(`agents/${agentId}/teams/stored/${teamId}/sync-members`, { method: 'POST' });
      const data = await res.json();
      
      if (data.success) {
        setSuccessMessage(data.message);
        await fetchMembers(teamId);
        await fetchTeams(); // Refresh counts
      } else {
        setError(data.error || 'Failed to sync members');
      }
    } catch (err) {
      setError('Failed to sync members');
    } finally {
      setSyncingMembers(null);
    }
  };

  // Toggle team enabled/disabled
  const handleToggleTeam = async (teamId: number, currentEnabled: boolean) => {
    setTogglingTeam(teamId);
    
    try {
      const res = await apiFetch(`agents/${agentId}/teams/stored/${teamId}/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: !currentEnabled }),
      });
      
      if (res.ok) {
        setTeams(prev => prev.map(t => 
          t.id === teamId ? { ...t, enabled: !currentEnabled } : t
        ));
      }
    } catch (err) {
      setError('Failed to toggle team');
    } finally {
      setTogglingTeam(null);
    }
  };

  // Toggle member enabled/disabled
  const handleToggleMember = async (memberId: number, teamId: number, currentEnabled: boolean) => {
    setTogglingMember(memberId);
    
    try {
      const res = await apiFetch(`agents/${agentId}/members/${memberId}/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: !currentEnabled }),
      });
      
      if (res.ok) {
        setMembersByTeam(prev => ({
          ...prev,
          [teamId]: prev[teamId]?.map(m => 
            m.id === memberId ? { ...m, enabled: !currentEnabled } : m
          ) || []
        }));
        await fetchTeams(); // Refresh counts
      }
    } catch (err) {
      setError('Failed to toggle member');
    } finally {
      setTogglingMember(null);
    }
  };

  // Handle accordion expand/collapse
  const handleAccordionChange = (teamId: number) => async (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    if (isExpanded) {
      setExpandedTeams(prev => new Set(prev).add(teamId));
      // Fetch members if not already loaded
      if (!membersByTeam[teamId]) {
        await fetchMembers(teamId);
      }
    } else {
      setExpandedTeams(prev => {
        const newSet = new Set(prev);
        newSet.delete(teamId);
        return newSet;
      });
    }
  };

  // Load teams on mount
  useEffect(() => {
    if (isConnected) {
      fetchTeams();
    }
  }, [isConnected, fetchTeams]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (!isConnected) {
    return (
      <Paper sx={{ p: 2, mt: 2, backgroundColor: '#f8fafc' }}>
        <Typography variant="body2" color="text.secondary">
          Connect Microsoft Teams to manage team members for quiz distribution.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <GroupsIcon sx={{ color: '#0099CC' }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Team Members
          </Typography>
          {teams.length > 0 && (
            <Chip 
              label={`${teams.reduce((sum, t) => sum + parseInt(t.enabled_members), 0)} active`}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Stack>
        <Button
          size="small"
          variant="outlined"
          startIcon={syncingTeams ? <CircularProgress size={16} /> : <SyncIcon />}
          onClick={handleSyncTeams}
          disabled={syncingTeams}
          sx={{
            borderColor: '#0099CC',
            color: '#0099CC',
            '&:hover': {
              borderColor: '#007AA3',
              backgroundColor: 'rgba(0, 153, 204, 0.04)',
            },
          }}
        >
          {syncingTeams ? 'Syncing...' : 'Sync Teams'}
        </Button>
      </Stack>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Loading state */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* No teams state */}
      {!loading && teams.length === 0 && (
        <Box textAlign="center" py={4}>
          <GroupsIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 1 }} />
          <Typography variant="body2" color="text.secondary" mb={2}>
            No teams synced yet
          </Typography>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={handleSyncTeams}
            disabled={syncingTeams}
            sx={{
              backgroundColor: '#0099CC',
              '&:hover': { backgroundColor: '#007AA3' },
            }}
          >
            Sync Teams from Microsoft
          </Button>
        </Box>
      )}

      {/* Teams list */}
      {!loading && teams.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Enable/disable teams and members to control who receives quizzes
          </Typography>
          
          {teams.map((team) => (
            <Accordion
              key={team.id}
              expanded={expandedTeams.has(team.id)}
              onChange={handleAccordionChange(team.id)}
              sx={{
                mb: 1,
                '&:before': { display: 'none' },
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                borderRadius: '8px !important',
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: team.enabled ? '#f8fafc' : '#fef2f2',
                  '&:hover': { backgroundColor: team.enabled ? '#f1f5f9' : '#fee2e2' },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} width="100%">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={team.enabled}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleTeam(team.id, team.enabled);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={togglingTeam === team.id}
                        sx={{
                          color: '#0099CC',
                          '&.Mui-checked': { color: '#0099CC' },
                        }}
                      />
                    }
                    label=""
                    sx={{ mr: 0 }}
                  />
                  <Box flex={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontWeight={600} sx={{ color: team.enabled ? '#2C3E50' : '#9CA3AF' }}>
                        {team.team_name}
                      </Typography>
                      {!team.enabled && (
                        <Chip label="Disabled" size="small" color="error" variant="outlined" />
                      )}
                    </Stack>
                    {team.team_description && (
                      <Typography variant="caption" color="text.secondary">
                        {team.team_description}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={`${team.enabled_members}/${team.total_members} members`}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                </Stack>
              </AccordionSummary>
              
              <AccordionDetails sx={{ p: 0 }}>
                <Divider />
                <Box sx={{ p: 2, backgroundColor: '#fff' }}>
                  {/* Sync members button */}
                  <Stack direction="row" justifyContent="flex-end" mb={1}>
                    <Button
                      size="small"
                      startIcon={syncingMembers === team.id ? <CircularProgress size={14} /> : <RefreshIcon />}
                      onClick={() => handleSyncMembers(team.id)}
                      disabled={syncingMembers === team.id}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {syncingMembers === team.id ? 'Syncing...' : 'Refresh Members'}
                    </Button>
                  </Stack>

                  {/* Members list */}
                  {!membersByTeam[team.id] ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : membersByTeam[team.id].length === 0 ? (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                      No members found. Click "Refresh Members" to sync.
                    </Typography>
                  ) : (
                    <List dense sx={{ py: 0 }}>
                      {membersByTeam[team.id].map((member) => (
                        <ListItem
                          key={member.id}
                          sx={{
                            borderRadius: '6px',
                            mb: 0.5,
                            backgroundColor: member.enabled ? 'transparent' : '#fef2f2',
                            '&:hover': {
                              backgroundColor: member.enabled ? '#f8fafc' : '#fee2e2',
                            },
                          }}
                          secondaryAction={
                            <Tooltip title={member.enabled ? 'Enabled - will receive quizzes' : 'Disabled - excluded from quizzes'}>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleToggleMember(member.id, team.id, member.enabled)}
                                disabled={togglingMember === member.id}
                              >
                                {togglingMember === member.id ? (
                                  <CircularProgress size={18} />
                                ) : member.enabled ? (
                                  <CheckCircleIcon sx={{ color: '#10B981' }} />
                                ) : (
                                  <CancelIcon sx={{ color: '#EF4444' }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <PersonIcon sx={{ color: member.enabled ? '#6B7280' : '#D1D5DB' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography 
                                variant="body2" 
                                fontWeight={500}
                                sx={{ color: member.enabled ? '#2C3E50' : '#9CA3AF' }}
                              >
                                {member.display_name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {member.email || 'No email'}
                                {member.job_title && ` • ${member.job_title}`}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default TeamMemberManagement;
